/* eslint new-cap: ["error", { "capIsNewExceptions": ["HOC"] }] */
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styles from './FloatingLabelInput.scss';
import { HOC } from 'formsy-react';
import styleVariables from '../../variables';
import formsyProps from '../FormsyProps';
import Textarea from 'react-textarea-autosize';

const FloatingLabelInput = class FloatingLabelInput extends React.Component {
  static propTypes = {
    autoHeight: PropTypes.bool,
    children: PropTypes.node,
    disabled: PropTypes.bool,
    inline: PropTypes.oneOf(['last', true, false]),
    inputRef: PropTypes.func,
    id: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    setValue: PropTypes.func,
    getValue: PropTypes.func,
    showError: PropTypes.func,
    hasValue: PropTypes.func,
    getErrorMessage: PropTypes.func,
    getErrorMessages: PropTypes.func,
    validationError: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    submitted: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.string
  };

  constructor(props) {
    super(props);
    this.state = {
      isActive: this.props.getValue() !== null && this.props.getValue() !== '',
      isFocused: false,
      initialBlur: false,
      id: this.getId()
    };
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.getValidationError = this.getValidationError.bind(this);
  }

  componentWillUnmount() {
    if (document && document.activeElement) {
      document.activeElement.blur();
    }
  }

  componentWillReceiveProps(nextProps) {
    // Ties `value` field to HOC value functions
    if (nextProps.value && nextProps.value !== this.props.getValue()) {
      this.props.setValue(nextProps.value);
      this.setState({ isActive: nextProps.value !== null && nextProps.value !== '' });
    }
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.getValue() === nextProps.value;
  }

  onChange(e, data) {
    const value = ((type) => {
      switch (type) {
        case 'select':
          return window.innerWidth >= styleVariables.tabletBreakpoint ? data.value : e.target.value;
        case 'currency':
          return e.floatValue;
        default:
          return e.target.value;
      }
    })(this.props.type);

    const changeEvent = ((type) => {
      switch (type) {
        case 'select':
          return Object.assign({}, e, { target: { value } });
        case 'currency':
          return Object.assign({}, data, { target: Object.assign(data.target, { value }) });
        default:
          return e;
      }
    })(this.props.type);

    this.setState({
      isActive: value !== ''
    });
    if (this.props.onChange) this.props.onChange(changeEvent);
    this.props.setValue(value);
  }

  onFocus(e) {
    this.setState({
      isFocused: true
    });
    if (this.props.onFocus) this.props.onFocus(e);
  }

  onBlur(e) {
    this.setState({
      isFocused: false,
      initialBlur: true
    });
    if (this.props.onBlur) this.props.onBlur(e);
  }

  getId() {
    return (
      this.props.id ||
      `${Math.random().toString(36)}00000000000000000`.slice(2, 16 + 2)
    );
  }

  getInitialStyles() {
    const initialStyles = [styles.floatingLabelInput];
    if (this.props.className) initialStyles.push(this.props.className);
    return initialStyles;
  }

  getValidationError() {
    const { getErrorMessage, getErrorMessages, validationError } = this.props;
    if (getErrorMessages().length > 0) {
      return (
        <div>
          {Object.values(getErrorMessages()).map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>);
    }
    return getErrorMessage() || validationError;
  }

  render() {
    const inputStyles = this.getInitialStyles();
    const { showError, hasValue } = this.props;
    const hasError =
      (this.state.initialBlur || this.props.submitted) &&
      (showError() || (this.props.required && !hasValue()));
    const errorMessage = hasError ? this.getValidationError() : null;

    if (this.props.disabled) {
      inputStyles.push(styles.disabled);
    }

    if (this.state.isActive) {
      inputStyles.push(styles.isActive);
    }

    if (this.state.isFocused) {
      inputStyles.push(styles.isFocused);
    }

    if (hasError) {
      inputStyles.push(styles.hasError);
    }

    if (this.props.inline) {
      inputStyles.push(styles.inline);
      if (this.props.inline === 'last') {
        inputStyles.push(styles.last);
      }
    }

    const placeholder =
      this.state.isFocused || !this.props.label
        ? this.props.placeholder
        : undefined;

    const eventHandlers = {
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur
    };

    // Remove props specific to this component
    // before passing them onto the child
    const _rest = _.omit(
      this.props,
      'autoHeight',
      'inline',
      'inputRef',
      'label',
      'children',
      'className',
      'onChange',
      'onFocus',
      'onBlur',
      'placeholder',
      'style',
      'type',
      'value',
      ...formsyProps
    );

    const type = this.props.type === 'currency' ? 'text' : this.props.type || 'text';

    return (
      <div
        className={[
          styles.field,
          this.props.inline ? styles.inlineBlock : '',
          this.props.className
        ].join(' ')}
        style={Object.assign(
          {},
          this.props.style,
          this.props.width ? { width: this.props.width } : {}
        )}
      >
        <div className={inputStyles.join(' ')}>
          <label htmlFor={this.state.id}>
            {this.props.label}
          </label>
          <div>
            {this.props.children && this.props.type !== 'currency' &&
              React.cloneElement(this.props.children, {
                type,
                id: this.state.id,
                placeholder,
                value: this.props.getValue(),
                ...eventHandlers,
                ..._rest
              })}
            {this.props.children && this.props.type === 'currency' &&
              React.cloneElement(this.props.children, {
                type: 'text',
                id: this.state.id,
                placeholder,
                value: this.props.getValue(),
                onFocus: this.onFocus,
                onBlur: this.onBlur,
                onValueChange: this.onChange,
                ..._rest
              })}
            {!this.props.children && !this.props.autoHeight &&
              <input
                type={type}
                id={this.state.id}
                value={this.props.getValue()}
                placeholder={placeholder}
                ref={this.props.inputRef}
                {...eventHandlers}
                {..._rest}
              />}
            {!this.props.children && this.props.autoHeight &&
              <Textarea
                id={this.state.id}
                value={this.props.getValue()}
                placeholder={placeholder}
                className={styles.textarea}
                rows={1}
                inputRef={this.props.inputRef}
                {...eventHandlers}
                {..._rest}
              />}
          </div>
        </div>
        <div className={styles.error}>
          {errorMessage}
        </div>
      </div>
    );
  }
};

export default HOC(FloatingLabelInput);



// WEBPACK FOOTER //
// ./webpack/components/Elements/FloatingLabelInput/FloatingLabelInput.js